
import { Options, Vue, prop } from "vue-class-component";
import formatInputMoneyWithDecimal from "@/app/infrastructures/misc/common-library/FormatInputMoneyWithDecimal";
import { LocationController } from "@/app/ui/controllers/LocationController";
import { CodInternationalCountry } from "@/domain/entities/CodConfiguration";
import { CountryData } from "@/domain/entities/Location";

const CURRENCY_SYMBOLS: Record<string, string> = {
  IDR: "Rp",
  MYR: "RM",
  USD: "$",
  SGD: "$",
  THB: "฿",
  PHP: "₱",
  VND: "₫",
  CNY: "¥",
  JPY: "¥",
  EUR: "€",
  AUD: "$",
  GBP: "£"
};

const resolveCurrencySymbol = (currencyCode: string): string => {
  const code = (currencyCode || "").toUpperCase();

  return CURRENCY_SYMBOLS[code] || code;
};

class Props {
  modelValue = prop<CodInternationalCountry[]>({
    default: () => []
  });

  isActiveStatus = prop<boolean>({
    default: false,
    type: Boolean
  });

  isValid = prop<boolean>({
    default: false,
    type: Boolean
  });
}

@Options({
  emits: ["update:modelValue", "validity-change", "update:isValid"],
  watch: {
    modelValue: {
      deep: true,
      handler: "onModelValueChange"
    },
    rows: {
      deep: true,
      handler: "onRowsChange"
    },
    isActiveStatus: {
      handler: "onActiveStatusChange"
    }
  }
})
class InternationalCountryTable extends Vue.with(Props) {
  rows: CodInternationalCountry[] = [];
  rowUidCounter = 0;
  lastEmittedJson = "";

  mounted() {
    this.buildRows(this.modelValue);

    this.$nextTick(() => {
      this.emitValidity();
    });

    LocationController.getCountryList({
      search: "",
      status: "active",
      page: 1,
      limit: 200
    });
  }

  get isLoadingCountry(): boolean {
    return LocationController.isLoadingCountry;
  }

  get countryOptions(): any[] {
    return LocationController.countryData.countryData || [];
  }

  get isTableValid(): boolean {
    if (this.isActiveStatus && this.rows.length === 0) {
      return false;
    }

    return this.rows.every((row) => this.isRowValid(row));
  }

  availableCountryOptions(row: CodInternationalCountry): any[] {
    return this.countryOptions.filter(
      (country: CountryData) =>
        country.id === row.countryId ||
        !this.rows.some(
          (other) => other.uid !== row.uid && other.countryId === country.id
        )
    );
  }

  selectedCountry(row: CodInternationalCountry): any {
    return (
      this.countryOptions.find(
        (country: any) => country.id === row.countryId
      ) || null
    );
  }

  formatCountryMoney(value: any, currencyCode: string): string {
    return formatInputMoneyWithDecimal(value, 2, currencyCode) as string;
  }

  toNumber(value: any, currencyCode: string): number {
    if (this.isEmptyValue(value)) {
      return 0;
    }

    return formatInputMoneyWithDecimal(value, 2, currencyCode, {
      reset: true
    }) as number;
  }

  isEmptyValue(value: any): boolean {
    return value === "" || value === null || value === undefined;
  }

  isRowComplete(row: CodInternationalCountry): boolean {
    return (
      !!row.countryId &&
      !this.isEmptyValue(row.minPrice) &&
      !this.isEmptyValue(row.maxPrice) &&
      !this.isEmptyValue(row.minCodFee) &&
      !this.isEmptyValue(row.posDeliveryCommissionPercentage)
    );
  }

  countryError(row: CodInternationalCountry): string {
    if (!row.countryTouched) {
      return "";
    }

    if (!row.countryId) {
      return `Lengkapi seluruh kolom pada negara ${row.countryName || "ini"}`;
    }

    return "";
  }

  minPriceError(row: CodInternationalCountry): string {
    if (!row.minPriceTouched) {
      return "";
    }

    if (this.isEmptyValue(row.minPrice)) {
      return `Lengkapi seluruh kolom pada negara ${row.countryName || "ini"}`;
    }

    const minPrice = this.toNumber(row.minPrice, row.currencyCode);

    if (!minPrice) {
      return "Minimum Harga Barang tidak boleh 0";
    }

    if (this.isEmptyValue(row.maxPrice)) {
      return "";
    }

    const maxPrice = this.toNumber(row.maxPrice, row.currencyCode);

    if (minPrice >= maxPrice) {
      return "Minimum Harga Barang harus lebih kecil dari Maksimum Harga Barang";
    }

    return "";
  }

  maxPriceError(row: CodInternationalCountry): string {
    if (!row.maxPriceTouched) {
      return "";
    }

    if (this.isEmptyValue(row.maxPrice)) {
      return `Lengkapi seluruh kolom pada negara ${row.countryName || "ini"}`;
    }

    const maxPrice = this.toNumber(row.maxPrice, row.currencyCode);

    if (maxPrice < 0) {
      return "Nilai tidak boleh kurang dari 0";
    }

    if (maxPrice === 0) {
      return "Maksimum Harga Barang tidak boleh 0";
    }

    if (this.isEmptyValue(row.minPrice)) {
      return "";
    }

    const minPrice = this.toNumber(row.minPrice, row.currencyCode);

    if (maxPrice <= minPrice) {
      return "Maksimum Harga Barang harus lebih besar dari Minimum Harga Barang";
    }

    return "";
  }

  minCodFeeError(row: CodInternationalCountry): string {
    if (!row.minCodFeeTouched) {
      return "";
    }

    if (this.isEmptyValue(row.minCodFee)) {
      return `Lengkapi seluruh kolom pada negara ${row.countryName || "ini"}`;
    }

    const minCodFee = this.toNumber(row.minCodFee, row.currencyCode);

    if (minCodFee < 0) {
      return "Nilai tidak boleh kurang dari 0";
    }

    return "";
  }

  commissionError(row: CodInternationalCountry): string {
    if (!row.posDeliveryCommissionPercentageTouched) {
      return "";
    }

    if (this.isEmptyValue(row.posDeliveryCommissionPercentage)) {
      return `Lengkapi seluruh kolom pada negara ${row.countryName || "ini"}`;
    }

    const commission = this.toNumber(
      row.posDeliveryCommissionPercentage,
      row.currencyCode
    );

    if (commission < 0) {
      return "Nilai tidak boleh kurang dari 0";
    }

    if (commission > 100) {
      return "Komisi COD POS Delivery maksimal 100";
    }

    return "";
  }

  isRowValid(row: CodInternationalCountry): boolean {
    if (!this.isRowComplete(row)) {
      return false;
    }

    const minPrice = this.toNumber(row.minPrice, row.currencyCode);
    const maxPrice = this.toNumber(row.maxPrice, row.currencyCode);
    const minCodFee = this.toNumber(row.minCodFee, row.currencyCode);
    const commission = this.toNumber(
      row.posDeliveryCommissionPercentage,
      row.currencyCode
    );

    return [
      minPrice > 0,
      maxPrice > 0,
      maxPrice > minPrice,
      minCodFee >= 0,
      commission >= 0,
      commission <= 100
    ].every(Boolean);
  }

  addRow() {
    this.rows.push(this.createEmptyRow());
  }

  removeRow(uid: number) {
    this.rows = this.rows.filter((row) => row.uid !== uid);
  }

  onSelectCountry(event: any, uid: number) {
    const row = this.rows.find((item) => item.uid === uid);

    if (!row) {
      return;
    }

    if (!event) {
      row.countryId = 0;
      row.countryName = "";
      row.currencyCode = "";
      row.currencySymbol = "";
      row.minPrice = "";
      row.maxPrice = "";
      row.minCodFee = "";
      row.posDeliveryCommissionPercentage = "";

      return;
    }

    const currencyChanged = row.currencyCode !== event.currency;

    row.countryId = event.id;
    row.countryName = event.name;
    row.currencyCode = event.currency;
    row.currencySymbol = resolveCurrencySymbol(event.currency);

    if (currencyChanged) {
      row.minPrice = "";
      row.maxPrice = "";
      row.minCodFee = "";
      row.posDeliveryCommissionPercentage = "";

      row.minPriceTouched = false;
      row.maxPriceTouched = false;
      row.minCodFeeTouched = false;
      row.posDeliveryCommissionPercentageTouched = false;
    }
  }

  createEmptyRow(): CodInternationalCountry {
    this.rowUidCounter += 1;

    return new CodInternationalCountry({
      uid: this.rowUidCounter,
      countryId: 0,
      countryName: "",
      currencyCode: "",
      currencySymbol: "",
      minPrice: "",
      maxPrice: "",
      minCodFee: "",
      posDeliveryCommissionPercentage: "",
      minPriceTouched: false,
      maxPriceTouched: false,
      minCodFeeTouched: false,
      posDeliveryCommissionPercentageTouched: false,
      countryTouched: false
    });
  }

  buildRows(list: CodInternationalCountry[]) {
    this.rows = (list || []).map((item) => this.toRow(item));
  }

  toRow(item: CodInternationalCountry): CodInternationalCountry {
    const currencyCode = item.currencyCode || "";

    this.rowUidCounter += 1;

    return new CodInternationalCountry({
      uid: this.rowUidCounter,
      countryId: item.countryId || 0,
      countryName: item.countryName || "",
      currencyCode,
      currencySymbol:
        item.currencySymbol || resolveCurrencySymbol(currencyCode),

      minPrice: this.isEmptyValue(item.minPrice)
        ? ""
        : this.formatCountryMoney(item.minPrice, currencyCode),

      maxPrice: this.isEmptyValue(item.maxPrice)
        ? ""
        : this.formatCountryMoney(item.maxPrice, currencyCode),

      minCodFee: this.isEmptyValue(item.minCodFee)
        ? ""
        : this.formatCountryMoney(item.minCodFee, currencyCode),

      posDeliveryCommissionPercentage: this.isEmptyValue(
        item.posDeliveryCommissionPercentage
      )
        ? ""
        : this.formatCountryMoney(
            item.posDeliveryCommissionPercentage,
            currencyCode
          )
    });
  }

  toModelValue(): CodInternationalCountry[] {
    return this.rows.map(
      (row) =>
        new CodInternationalCountry({
          countryId: row.countryId,
          countryName: row.countryName,
          currencyCode: row.currencyCode,
          currencySymbol: row.currencySymbol,
          minPrice: this.toModelNumber(row.minPrice, row.currencyCode),
          maxPrice: this.toModelNumber(row.maxPrice, row.currencyCode),
          minCodFee: this.toModelNumber(row.minCodFee, row.currencyCode),
          posDeliveryCommissionPercentage: this.toModelNumber(
            row.posDeliveryCommissionPercentage,
            row.currencyCode
          )
        })
    );
  }

  toModelNumber(value: any, currencyCode: string): any {
    if (this.isEmptyValue(value)) {
      return null;
    }

    return this.toNumber(value, currencyCode);
  }

  emitValidity() {
    this.$emit("validity-change", this.isTableValid);
    this.$emit("update:is-valid", this.isTableValid);
  }

  onRowsChange() {
    const model = this.toModelValue();
    const modelJson = JSON.stringify(model);

    if (modelJson !== this.lastEmittedJson) {
      this.lastEmittedJson = modelJson;
      this.$emit("update:modelValue", model);
    }

    this.emitValidity();
  }

  onModelValueChange() {
    const incoming = JSON.stringify(this.modelValue || []);

    if (incoming === this.lastEmittedJson) {
      return;
    }

    this.buildRows(this.modelValue);

    this.$nextTick(() => {
      this.emitValidity();
    });
  }

  onActiveStatusChange() {
    this.emitValidity();
  }
}

export default InternationalCountryTable;
